import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "../../components/Grid/Grid";
import { CUSTOM_MESSAGES, PAGE_SIZES } from "../../constants/globalConstants";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import MainHeading from "../../components/MainHeading/MainHeading";
import CustomButton from "../../components/CustomButton/CustomButton";
import ThreeDotActionItem from "../../components/ThreeDotActionItem/ThreeDotActionItem";
import useGridHeight from "../../hooks/useGridHeight";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { useLazyGetFirmwareListQuery } from "../../app/api/firmwareApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useAppSelector } from "../../redux/store";
import { useFilterManager } from "../../hooks/useFilterManager";

const FirmwarePortal = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const { handleFilterChange } = useFilterManager();
  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );

  const [brandId, setBrandId] = useState<string | null>(
    persistDropdownSelector["brand_selector"]?.value ?? null
  );
  const [familyId, setFamilyId] = useState<string | null>(
    persistDropdownSelector["family_selector"]?.value ?? null
  );
  const navigate = useNavigate();
  useGridHeight();
  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [getFirmwareListQuery, firmwareListResponse] =
    useLazyGetFirmwareListQuery();

  const pageSizeRef = useRef(String(PAGE_SIZES.Default_List_Size));
  const tableHeaders = [
    {
      headerName: "Provisioning URL",
      shouldSort: true,
    },
    {
      headerName: "Firmware Version",
      shouldSort: true,
    },
    {
      headerName: "Unique ID",
      shouldSort: true,
    },
    {
      headerName: "Brand Name",
      shouldSort: true,
    },
    {
      headerName: "Family Name",
      shouldSort: true,
    },

    {
      headerName: "Status",
    },
    { headerName: "" },
  ];

  const asyncFormSelectProps = [
    {
      label: "Brand",
      name: "brand_name",
      onChange: (val: string | null, label: string) => {
        if (val !== brandId) {
          setBrandId(val);
          setFamilyId(null);
          handleFilterChange({
            updatedField: "brand_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getBrandListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
      },
      optionLabelName: "brand_name",
      placeholder: "Select Brand",
      shouldFetch: true,
      persistedOption: persistDropdownSelector["brand_selector"]
        ? {
            label: persistDropdownSelector["brand_selector"]?.label,
            value: persistDropdownSelector["brand_selector"]?.value,
          }
        : null,
    },
    {
      label: "Family",
      name: "family_name",
      onChange: (val: string | null, label: string) => {
        setFamilyId(val);
        handleFilterChange({
          updatedField: "family_id",
          persistedSelection: {
            label,
            value: val,
          },
        });
      },
      fetchQuery: getFamilyListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
      },
      optionLabelName: "family_name",
      placeholder: "Select Family",
      key: brandId,
      shouldFetch: brandId?.length,
      persistedOption: persistDropdownSelector["family_selector"]
        ? {
            label: persistDropdownSelector["family_selector"]?.label,
            value: persistDropdownSelector["family_selector"]?.value,
          }
        : null,
    },
  ];

  const handleGetFirmwareList = async () => {
    try {
      const response = await getFirmwareListQuery({
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
      }).unwrap();

      if (response?.status_code === 200) {
        setTableData(response?.data);
      }
    } catch (error) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Fetch Firmware List Error",
      });
    }
  };

  useEffect(() => {
    if (brandId && familyId) {
      handleGetFirmwareList();
    } else {
      setTableData([]);
    }
    // eslint-disable-next-line
  }, [brandId, familyId]);

  const updatedTableData = (tableData ?? []).map((tdata) => ({
    "Provisioning URL": tdata?.provisioning_url,
    "Firmware Version": tdata?.firmware_version,
    "Unique ID": tdata.id,
    "Brand Name": tdata.brand_name,
    "Family Name": tdata?.family_name,
    Status: [
      tdata.is_active
        ? { text: "Active", color: "#12da85" }
        : { text: "Inactive", color: "#bbbbbb" },
    ],
    actionIcon: (
      <ThreeDotActionItem
        actionItemsMenuPositionStyles={{
          right: "15px",
          left: "unset",
        }}
        actionIconSubItemsList={[
          {
            actionItemName: "View Details",
            onClick: (id: string) =>
              navigate(`/provisioning/firmware/create?id=${tdata.id}`),
          },
          {
            actionItemName: "Edit Details",
            onClick: (id: string) =>
              navigate(`/provisioning/firmware/edit?id=${tdata.id}`),
          },
        ]}
        id={tdata.id}
      />
    ),
  }));

  const handleLoadMoreClick = async () => {
    try {
      if (
        firmwareListResponse.isSuccess &&
        firmwareListResponse.data?.next_start_key
      ) {
        const response = await getFirmwareListQuery(
          {
            startKey: firmwareListResponse.data?.next_start_key ?? "",
            page_size: pageSizeRef.current,
            brand_id: brandId,
            family_id: familyId,
          },
          true
        );

        if (response && response?.data?.data) {
          setTableData((prev) => [...prev, ...response?.data?.data]);
        }
      }
    } catch (error) {
      console.error(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Load More Firmware List Error",
      });
    }
  };

  return (
    <>
      <Layout>
        <MainHeading headerText="Firmware" subHeaderText={"Description"}>
          <CustomButton
            isSubmitting={false}
            label="Create Firmware"
            customClass="w-11rem px-1 py-0 m-0 h-48 fs-6"
            onClick={() => navigate("create")}
            disabled={false}
          />
        </MainHeading>

        <Grid
          tableHeaders={tableHeaders}
          tableData={updatedTableData}
          isLoading={firmwareListResponse.isLoading}
          isDataPresent={tableData?.length > 0}
          isError={firmwareListResponse.isError}
          error={firmwareListResponse.error}
          noDataUIMainText={CUSTOM_MESSAGES.No_Firmware_Yet}
          noDataUISubText={CUSTOM_MESSAGES.Create_Firmware_Now}
          noDataUIButtonLabel="Create Firmware"
          showPageSizeSelector
          page_size={pageSizeRef.current}
          handlePageSizeChange={(val) => {
            if (val !== pageSizeRef.current) {
              pageSizeRef.current = val as string;
              handleGetFirmwareList();
            }
          }}
          sort={{ sortField: "Provisioning URL", sortOrder: "asc" }}
          handleClick={() => navigate("create")}
          asyncFormSelectProps={asyncFormSelectProps}
        />

        {((firmwareListResponse.isSuccess &&
          firmwareListResponse?.data?.next_start_key) ||
          firmwareListResponse.isFetching) && (
          <div className="text-center" id="loadmore-button">
            <CustomButton
              isSubmitting={firmwareListResponse.isFetching}
              label="Load More"
              customClass="px-1 mw-100 py-0 m-0 mt-3 fs-6"
              customStyles={{ width: "30rem" }}
              onClick={handleLoadMoreClick}
            />
          </div>
        )}
      </Layout>
    </>
  );
};

export default FirmwarePortal;
